import React from "react";
import { Row, Col, Card, CardBody, Badge } from "reactstrap";
import {
  FaUser,
  FaBuilding,
  FaMapMarkerAlt,
  FaUniversity,
  FaFileInvoice,
  FaFileAlt,
  FaAddressBook,
} from "react-icons/fa";

const ProfileTab = ({ partnerDetails }) => {
  const {
    businessName,
    fullName,
    email,
    phoneNumber,
    panNumber,
    city,
    state,
    category,
    type,
    status,
    addresses = [],
    bankAccounts = [],
    gstDetails = [],
    documents = [],
    contacts = [],
  } = partnerDetails || {};

  const InfoItem = ({ label, value }) => (
    <p className="mb-1">
      <strong className="text-dark">{label}:</strong>{" "}
      <span className="text-secondary fw-semibold">{value || "—"}</span>
    </p>
  );

  const SectionCard = ({ icon: Icon, title, color, children }) => (
    <Card
      className="border-0 shadow-lg rounded-4 h-100 card-glow hover-zoom"
      style={{
        background: "rgba(255,255,255,0.9)",
        backdropFilter: "blur(6px)",
      }}
    >
      <div
        className="px-3 py-2 text-white fw-semibold rounded-top"
        style={{
          background: `linear-gradient(90deg, ${color}, #4b6cb7)`,
          borderTopLeftRadius: "1rem",
          borderTopRightRadius: "1rem",
        }}
      >
        <Icon className="me-2 mb-1" /> {title}
      </div>
      <CardBody>{children}</CardBody>
    </Card>
  );

  return (
    <div className="profile-tab-container mt-3">
      <Row className="gy-4">
        {/* Basic Information */}
        <Col xs={12} lg={6}>
          <SectionCard icon={FaUser} title="Basic Information" color="#6a11cb">
            <InfoItem label="Business Name" value={businessName} />
            <InfoItem label="Full Name" value={fullName} />
            <InfoItem label="Email" value={email} />
            <InfoItem label="Phone" value={phoneNumber} />
            <InfoItem label="PAN Number" value={panNumber} />
            <InfoItem label="City" value={city} />
            <InfoItem label="State" value={state} />
            <InfoItem label="Category" value={category} />
            <InfoItem label="Type" value={type} />
            <p className="mb-0 mt-2">
              <strong>Status:</strong>{" "}
              <Badge color={status ? "success" : "danger"} pill>
                {status ? "Active" : "Inactive"}
              </Badge>
            </p>
          </SectionCard>
        </Col>

        {/* Addresses */}
        <Col xs={12} lg={6}>
          <SectionCard icon={FaMapMarkerAlt} title="Addresses" color="#11998e">
            {addresses.length > 0 ? (
              addresses.map((address, index) => (
                <div
                  key={index}
                  className="border-start border-4 border-primary ps-3 mb-3 bg-light-subtle rounded py-2"
                >
                  <p className="fw-semibold text-dark mb-1">
                    {address.addressType}
                  </p>
                  <p className="mb-0">
                    {address.addressLine1} {address.addressLine2}
                  </p>
                  <small className="text-muted">
                    {address.city}, {address.state} - {address.pincode}
                  </small>
                </div>
              ))
            ) : (
              <p className="text-muted mb-0">No addresses added</p>
            )}
          </SectionCard>
        </Col>

        {/* Bank Details */}
        <Col xs={12} lg={6}>
          <SectionCard icon={FaUniversity} title="Bank Details" color="#f7971e">
            {bankAccounts.length > 0 ? (
              bankAccounts.map((bank, index) => (
                <div
                  key={index}
                  className="border-start border-4 border-success ps-3 mb-2 bg-light-subtle rounded py-2"
                >
                  <InfoItem label="Account Number" value={bank.accountNumber} />
                  <InfoItem label="Bank Name" value={bank.bankName} />
                </div>
              ))
            ) : (
              <p className="text-muted mb-0">No bank details added</p>
            )}
          </SectionCard>
        </Col>

        {/* GST Details */}
        <Col xs={12} lg={6}>
          <SectionCard icon={FaFileInvoice} title="GST Details" color="#ff6a00">
            {gstDetails.length > 0 ? (
              gstDetails.map((gst, index) => (
                <div
                  key={index}
                  className="border-start border-4 border-warning ps-3 mb-2 bg-light-subtle rounded py-2"
                >
                  <InfoItem label="GST Number" value={gst.gstNumber} />
                  <p className="mb-0">
                    <strong>Available:</strong>{" "}
                    <Badge color={gst.isAvailable ? "success" : "danger"} pill>
                      {gst.isAvailable ? "Yes" : "No"}
                    </Badge>
                  </p>
                </div>
              ))
            ) : (
              <p className="text-muted mb-0">No GST details available</p>
            )}
          </SectionCard>
        </Col>
        <Col xs={12} lg={6}>
          <SectionCard icon={FaFileAlt} title="Documents" color="#00b09b">
            {documents.length > 0 ? (
              documents.map((doc, index) => (
                <div
                  key={index}
                  className="border-start border-4 border-info ps-3 mb-3 bg-light-subtle rounded py-2"
                >
                  <img
                    src={
                      "https://india-tourism.s3.ap-south-1.amazonaws.com" +
                      `/${doc?.docUrl}`
                    }
                    height={45}
                    width={45}
                  />
                </div>
              ))
            ) : (
              <p className="text-muted mb-0">No documents uploaded</p>
            )}
          </SectionCard>
        </Col>
        <Col xs={12} lg={6}>
          <SectionCard icon={FaAddressBook} title="Contacts" color="#ee0979">
            {contacts.length > 0 ? (
              contacts.map((contact, index) => (
                <div
                  key={index}
                  className="border-start border-4 border-secondary ps-3 mb-2 bg-light-subtle rounded py-2"
                >
                  <InfoItem label="Name" value={contact.name} />
                  <InfoItem label="Designation" value={contact.designation} />
                  <InfoItem label="Phone" value={contact.phone} />
                  <InfoItem label="Email" value={contact.email} />
                </div>
              ))
            ) : (
              <p className="text-muted mb-0">No contact persons added</p>
            )}
          </SectionCard>
        </Col>
      </Row>
      <style>{`
        .card-glow {
          transition: all 0.3s ease;
        }
        .card-glow:hover {
          transform: translateY(-4px);
          box-shadow: 0 6px 20px rgba(0,0,0,0.15);
        }
        .hover-zoom:hover {
          scale: 1.01;
        }
        .text-gradient {
          background: linear-gradient(90deg, #6a11cb, #2575fc);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
      `}</style>
    </div>
  );
};

export default ProfileTab;
